import { CoachAdminDto } from '../../../models/coach/dto/coach-admin.dto';
import {
    composeGuestPhoneE164,
    DEFAULT_GUEST_PHONE_CALLING_CODE,
    isPlausibleGuestEmail,
} from '../../court-details/utils/guest-player';

/**
 * MYS-75 — what the operator is typing into the coach's contact fields. The
 * phone is kept as TYPED (plus the picker's calling code) and only composed to
 * E.164 on submit, with the same helper MYS-140's guest form uses (D3): a
 * stored number is already E.164 and is shown as is, and a "+…" value wins
 * over the picker, so it round-trips unchanged.
 */
export type CoachContactDraft = {
    callingCode: string;
    phone: string;
    isPhonePublic: boolean;
    notificationEmail: string;
};

export type CoachContactPayload = {
    phone: string | null;
    isPhonePublic: boolean;
    notificationEmail: string | null;
};

export const EMPTY_COACH_CONTACT_DRAFT: CoachContactDraft = {
    callingCode: DEFAULT_GUEST_PHONE_CALLING_CODE,
    phone: '',
    isPhonePublic: false,
    notificationEmail: '',
};

export function coachContactDraftFrom(coach: CoachAdminDto): CoachContactDraft {
    return {
        callingCode: DEFAULT_GUEST_PHONE_CALLING_CODE,
        phone: coach.phone ?? '',
        isPhonePublic: Boolean(coach.isPhonePublic),
        notificationEmail: coach.notificationEmail ?? '',
    };
}

/**
 * The fields to send, or `null` while one of them cannot be sent. A blank
 * field is `null`, which the API reads as "clear it" (MYS-75 T2's tri-state
 * contract); the server stays the judge of both formats, this only stops a
 * value it will refuse.
 */
export function coachContactPayload(draft: CoachContactDraft): CoachContactPayload | null {
    const errors = coachContactErrors(draft);
    if (errors.phone || errors.notificationEmail) return null;
    const email = draft.notificationEmail.trim();
    return {
        phone: draft.phone.trim() ? composeGuestPhoneE164(draft.callingCode, draft.phone) : null,
        isPhonePublic: draft.isPhonePublic,
        notificationEmail: email ? email : null,
    };
}

export function coachContactErrors(draft: CoachContactDraft): { phone: boolean; notificationEmail: boolean } {
    const email = draft.notificationEmail.trim();
    return {
        phone: draft.phone.trim().length > 0 && !composeGuestPhoneE164(draft.callingCode, draft.phone),
        notificationEmail: email.length > 0 && !isPlausibleGuestEmail(email),
    };
}
